import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BookingCalendar from "@/components/BookingCalendar";
import BookingDialog from "@/components/BookingDialog";
import UpcomingBookings from "@/components/UpcomingBookings";
import ActivityFeed from "@/components/ActivityFeed";
import HouseNotes from "@/components/HouseNotes";
import CalendarSync from "@/components/CalendarSync";
import WhatsAppNudge from "@/components/WhatsAppNudge";
import FamilyDirectory from "@/components/FamilyDirectory";

const TABS = [
  { id: "calendar", label: "Calendar" },
  { id: "notes", label: "House Notes" },
  { id: "family", label: "Family" },
];

const HERO = {
  calendar: {
    kicker: "A guestbook for the house",
    title: "Pick the days the hills will hold you.",
    body: "Drag across the calendar to block your stay. Everyone in the family gets a note in their inbox, so the house is never double-booked.",
  },
  notes: {
    kicker: "The single source of truth",
    title: "The house, in one little book.",
    body: "Wifi, keys, the caretaker's number, what to do before you leave. Anyone in the family can keep it up to date.",
  },
  family: {
    kicker: "Everyone the house can reach",
    title: "The family, and how to find them.",
    body: "Emails and WhatsApp numbers for alerts. Fill in a number for anyone, then send them the one-tap join link.",
  },
};

export default function Dashboard({ session, onLogout, onSessionUpdate }) {
  const [bookings, setBookings] = useState([]);
  const [activity, setActivity] = useState([]);
  const [family, setFamily] = useState([]);
  const [dialogRange, setDialogRange] = useState(null);
  const [tab, setTab] = useState("calendar");
  const [waSetup, setWaSetup] = useState(null);

  const refresh = useCallback(async () => {
    const [b, a, f] = await Promise.all([
      api.getBookings(),
      api.getActivity(),
      api.getFamily(),
    ]);
    setBookings(b);
    setActivity(a);
    setFamily(f);
  }, []);

  useEffect(() => {
    refresh();
    api.getWhatsAppSetup().then(setWaSetup).catch(() => setWaSetup({ enabled: false }));
  }, [refresh]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const upcoming = useMemo(
    () => bookings.filter((b) => b.end_date >= today),
    [bookings, today]
  );

  const handleRangeSelect = (range) => {
    if (!range?.from || !range?.to) return;
    setDialogRange(range);
  };

  const handleBookingCreate = async (note, blockType = "hard") => {
    if (!dialogRange) return;
    const toIso = (d) => {
      const x = new Date(d);
      x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
      return x.toISOString().slice(0, 10);
    };
    try {
      await api.createBooking({
        member_name: session.name,
        email: session.email,
        start_date: toIso(dialogRange.from),
        end_date: toIso(dialogRange.to),
        note,
        block_type: blockType,
      });
      toast.success(
        blockType === "soft"
          ? "Pencilled in. We'll remind you 30 and 15 days out."
          : "Booked. The family has been told."
      );
      setDialogRange(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.cancelBooking(id, session.name);
      toast.success("Booking cancelled. The family has been told.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
  };

  const handleConfirm = async (id) => {
    try {
      await api.confirmBooking(id, session.name);
      toast.success("Confirmed. It's a hard block now.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm");
    }
  };

  const handleNudge = async (id) => {
    try {
      const r = await api.nudgeBooking(id, session.name);
      toast.success(`Nudge sent to ${r.to}.`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not nudge");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="dashboard">
      {/* Sticky Nav */}
      <nav
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#DCE0D9]/70 safe-top"
        data-testid="top-nav"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/assets/stjames-wordmark.png"
              alt="StJames"
              data-testid="brand-logo"
              className="h-9 sm:h-10 w-auto select-none"
              draggable="false"
            />
            <span className="hidden sm:inline tracker text-[10px] text-[#5C6B61] ml-1">
              Mahabaleshwar
            </span>
          </div>
          <div className="flex items-center gap-5">
            <span
              className="inline-flex items-center gap-2 font-body text-sm text-[#5C6B61]"
              data-testid="session-greeting"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: session.color }}
              />
              <span className="hidden sm:inline">welcome,</span>{" "}
              <span className="text-[#1A2F24]">{session.name}</span>
            </span>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="font-body text-xs tracker text-[#9E473F] hover:text-[#1A2F24] transition-colors duration-300 min-h-[44px] px-1"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
          <div className="flex gap-6 sm:gap-8">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`relative py-4 font-body text-xs tracker transition-colors duration-300 min-h-[48px] ${
                    active ? "text-[#1A2F24]" : "text-[#5C6B61] hover:text-[#1A2F24]"
                  }`}
                >
                  {t.label}
                  {active && (
                    <motion.span
                      layoutId="tab-underline"
                      className="absolute left-0 right-0 -bottom-px h-px bg-[#1A2F24]"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pt-8 sm:pt-14 pb-6 sm:pb-10">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="tracker text-[10px] sm:text-[11px] text-[#9E473F]">{HERO[tab].kicker}</p>
          <h1 className="font-display text-[2rem] leading-[1.05] sm:text-5xl lg:text-7xl mt-2 sm:mt-3 text-[#1A2F24] max-w-4xl">
            {HERO[tab].title}
          </h1>
          <p className="font-body text-sm sm:text-base text-[#5C6B61] mt-4 sm:mt-5 max-w-xl">{HERO[tab].body}</p>
        </motion.div>
      </header>

      <AnimatePresence mode="wait">
        {tab === "calendar" ? (
          <motion.main
            key="calendar"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pb-16 sm:pb-24 space-y-6 sm:space-y-10"
          >
            <WhatsAppNudge session={session} onSessionUpdate={onSessionUpdate} onGoToFamily={() => setTab("family")} />
            <section
              className="bg-white border border-[#DCE0D9] p-5 sm:p-6 md:p-10"
              data-testid="calendar-card"
            >
              <div className="flex items-end justify-between flex-wrap gap-3 mb-6 sm:mb-8">
                <div>
                  <p className="tracker text-[10px] text-[#5C6B61]">
                    The Calendar
                  </p>
                  <h2 className="font-display text-2xl sm:text-3xl text-[#1A2F24] mt-1">
                    Block your dates
                  </h2>
                </div>
                <p className="font-body text-xs text-[#5C6B61] max-w-sm">
                  Tap a start day, then an end day. Solid underline = hard
                  block, dotted = soft (tentative).
                </p>
              </div>

              <BookingCalendar
                bookings={bookings}
                onRangeSelect={handleRangeSelect}
              />

              {/* Family legend */}
              <div className="mt-8 pt-6 border-t border-[#DCE0D9]">
                <p className="tracker text-[10px] text-[#5C6B61] mb-4">
                  The Family
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-3">
                  {family.map((m) => (
                    <span
                      key={m.name}
                      className="inline-flex items-center gap-2 font-body text-sm text-[#1A2F24]"
                      data-testid={`legend-${m.name.toLowerCase()}`}
                    >
                      <span
                        className="inline-block h-2 w-6"
                        style={{ background: m.color }}
                      />
                      {m.name}
                    </span>
                  ))}
                </div>
              </div>

              <CalendarSync memberName={session.name} />
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <UpcomingBookings
                bookings={upcoming}
                currentName={session.name}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                onNudge={handleNudge}
              />
              <ActivityFeed items={activity} />
            </div>
          </motion.main>
        ) : tab === "notes" ? (
          <motion.main
            key="notes"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pb-16 sm:pb-24"
          >
            <HouseNotes session={session} />
          </motion.main>
        ) : (
          <motion.main
            key="family"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pb-16 sm:pb-24"
          >
            <FamilyDirectory family={family} session={session} setup={waSetup} onChanged={refresh} />
          </motion.main>
        )}
      </AnimatePresence>

      <BookingDialog
        open={!!dialogRange}
        range={dialogRange}
        memberName={session.name}
        onClose={() => setDialogRange(null)}
        onConfirm={handleBookingCreate}
      />
    </div>
  );
}
